import React, { useEffect, useState } from 'react';
import { MegaphoneIcon, ArrowLeftIcon, EditIcon, TrashIcon, InfoIcon, CalendarIcon, TagIcon, DollarSignIcon, PercentIcon, UserIcon, BuildingIcon, CreditCardIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, DownloadIcon, XIcon, CheckIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/campaigns/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    companies: ['Rabbit Life'],
    transactionVolume: 0,
    transactionCount: 0
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (10 THB Discount)',
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  }
};
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-2518" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-2519" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-2520" />
  }];
  useEffect(() => {
    // In a real app, we would fetch the campaign data from an API
    // For now, we'll use the URL parameter to get the campaign ID
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    window.location.href = '/campaigns';
  };
  const navigateToEdit = () => {
    if (campaign) {
      window.location.href = `/campaign-form?id=${campaign.id}`;
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-2521">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-2522"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-2523">
        <h2 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-2524">
          Campaign not found
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-2525">
          The campaign you are looking for does not exist or has been removed.
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2526">
          Back to Campaigns
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2527">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2528">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2529">
                Conversion Funnel
              </h3>
              <ConversionFunnelChart data-id="element-2530" />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2531">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2532">
                Conversion Rates
              </h3>
              <ConversionRatePieChart data-id="element-2533" />
            </div>
          </div>;
      case 'usage':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2534">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2535">
              Code Usage Over Time (Last 30 Days)
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-2536" />
          </div>;
      case 'volume':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2537">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2538">
              Payment Volume Over Time (Last 30 Days)
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-2539" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-2540">
      {/* Top bar with actions */}
      <div className="flex justify-between items-center" data-id="element-2541">
        <button onClick={navigateBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-2542">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-2543" />
          <span data-id="element-2544">Back to Campaigns</span>
        </button>
        <div className="flex space-x-3" data-id="element-2545">
          <button onClick={() => setShowExportModal(true)} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-2546">
            <DownloadIcon size={16} className="mr-2" data-id="element-2547" />
            Export CSV
          </button>
          <button onClick={navigateToEdit} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-2548">
            <EditIcon size={16} className="mr-2" data-id="element-2549" />
            Edit Campaign
          </button>
          <button className="px-3 py-2 bg-white border border-red-500 text-red-500 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-2550">
            <TrashIcon size={16} className="mr-2" data-id="element-2551" />
            Delete
          </button>
        </div>
      </div>
      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2552">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-2553">
            <div className="flex justify-between items-center mb-4" data-id="element-2554">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2555">
                Export Campaign Data
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} className="text-gray-400 hover:text-gray-500" data-id="element-2556">
                <XIcon size={20} data-id="element-2557" />
              </button>
            </div>
            <div className="mb-4" data-id="element-2558">
              <p className="text-sm text-gray-600 mb-2" data-id="element-2559">
                Select time period for export:
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-2560" />
            </div>
            <div className="mb-4" data-id="element-2561">
              <p className="text-sm text-gray-600" data-id="element-2562">
                The export will include all campaign data for the selected
                period:
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-gray-600" data-id="element-2563">
                <li data-id="element-2564">Daily code usage statistics</li>
                <li data-id="element-2565">Conversion metrics</li>
                <li data-id="element-2566">Transaction volumes and amounts</li>
                <li data-id="element-2567">User engagement data</li>
              </ul>
            </div>
            <div className="flex justify-end" data-id="element-2568">
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-2569">
                  <DownloadIcon size={16} className="mr-2" data-id="element-2570" />
                  Export
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-blue-600 text-white rounded-md opacity-70 flex items-center" data-id="element-2571">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" data-id="element-2572"></div>
                  Exporting...
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-green-600 text-white rounded-md flex items-center" data-id="element-2573">
                  <CheckIcon size={16} className="mr-2" data-id="element-2574" />
                  Export Complete
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-2575">
                  Try Again
                </button>}
            </div>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2576">
        <div className="flex items-center mb-6" data-id="element-2577">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-2578">
            <MegaphoneIcon size={24} className="text-blue-600" data-id="element-2579" />
          </div>
          <div data-id="element-2580">
            <h1 className="text-2xl font-bold text-gray-800" data-id="element-2581">
              {campaign.title}
            </h1>
            <p className="text-gray-500" data-id="element-2582">Campaign ID: {campaign.id}</p>
          </div>
          <div className="ml-auto" data-id="element-2583">
            <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : campaign.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2584">
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
          </div>
        </div>
        {/* Campaign statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2585">
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-2586">
            <div className="flex items-center justify-between mb-4" data-id="element-2587">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-2588">
                Total Budget
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-2589" />
            </div>
            <div className="flex items-center mb-2" data-id="element-2590">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-2591">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: `${calculateBudgetUsage()}%`
              }} data-id="element-2592"></div>
              </div>
              <span className="text-sm text-gray-500" data-id="element-2593">
                {calculateBudgetUsage()}%
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-2594">
              {campaign.budgetUsed.toLocaleString()} /{' '}
              {campaign.budget.toLocaleString()} {campaign.currency}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-2595">
            <div className="flex items-center justify-between mb-4" data-id="element-2596">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-2597">
                Transaction volume for campaign
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-2598" />
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-2599">
              {campaign.currency}{' '}
              {campaign.transactionVolume?.toLocaleString() || '0.00'}
            </div>
            <div className="text-xs text-gray-500 mt-1" data-id="element-2600">
              {campaign.transactionCount || 0} transactions have been collected
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-2601">
            <div className="flex items-center justify-between mb-4" data-id="element-2602">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-2603">KPI</h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-2604" />
            </div>
            <div className="space-y-4" data-id="element-2605">
              <div data-id="element-2606">
                <p className="text-xs text-gray-500" data-id="element-2607">Transaction volume ROI</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-2608">0</p>
                <p className="text-xs text-gray-500" data-id="element-2609">
                  Against a budget of {campaign.currency} 0.00
                </p>
              </div>
              <div data-id="element-2610">
                <p className="text-xs text-gray-500" data-id="element-2611">Transactions</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-2612">0</p>
                <p className="text-xs text-gray-500" data-id="element-2613">0 Merchants</p>
              </div>
            </div>
          </div>
        </div>
        {/* Campaign Analytics Section */}
        <div className="border-t border-gray-200 pt-6 mb-8" data-id="element-2614">
          <div className="flex justify-between items-center mb-6" data-id="element-2615">
            <h2 className="text-lg font-medium text-gray-800 flex items-center" data-id="element-2616">
              <PieChartIcon size={20} className="mr-2 text-blue-600" data-id="element-2617" />
              Campaign Analytics
            </h2>
          </div>
          {/* Analytics Tabs */}
          <div className="mb-6" data-id="element-2618">
            <div className="border-b border-gray-200" data-id="element-2619">
              <nav className="flex -mb-px space-x-8" data-id="element-2620">
                {analyticsTabs.map(tab => <button key={tab.id} className={`py-4 px-1 flex items-center text-sm font-medium ${activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab(tab.id)} data-id="element-2621">
                    <span className="mr-2" data-id="element-2622">{tab.icon}</span>
                    {tab.label}
                  </button>)}
              </nav>
            </div>
          </div>
          {/* Analytics Content */}
          {renderAnalyticsContent()}
        </div>
        {/* Campaign details */}
        <div className="border-t border-gray-200 pt-6" data-id="element-2623">
          <div className="flex justify-between items-center mb-4" data-id="element-2624">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-2625">
              Campaign details
            </h2>
            <button onClick={navigateToEdit} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-2626">
              <EditIcon size={14} className="mr-1" data-id="element-2627" />
              Edit details
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-2628">
            <div data-id="element-2629">
              <h3 className="text-sm text-gray-500" data-id="element-2630">Title</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2631">
                {campaign.title}
              </p>
            </div>
            <div data-id="element-2632">
              <h3 className="text-sm text-gray-500" data-id="element-2633">Category</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2634">
                {campaign.category}
              </p>
            </div>
            <div data-id="element-2635">
              <h3 className="text-sm text-gray-500" data-id="element-2636">Conditions</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2637">
                {campaign.conditions || 'No information'}
              </p>
            </div>
            <div data-id="element-2638">
              <h3 className="text-sm text-gray-500" data-id="element-2639">Expiry Date</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2640">
                {campaign.expiryDate}
              </p>
            </div>
            <div data-id="element-2641">
              <h3 className="text-sm text-gray-500" data-id="element-2642">Description</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2643">
                {campaign.description}
              </p>
            </div>
            <div data-id="element-2644">
              <h3 className="text-sm text-gray-500" data-id="element-2645">Campaign Owner</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2646">
                {campaign.owner}
              </p>
            </div>
            <div data-id="element-2647">
              <h3 className="text-sm text-gray-500" data-id="element-2648">Code Strategy</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2649">
                {campaign.codeStrategy}
              </p>
            </div>
            <div data-id="element-2650">
              <h3 className="text-sm text-gray-500" data-id="element-2651">Min. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2652">
                {campaign.currency} {campaign.minTransactionValue.toFixed(2)}
              </p>
            </div>
            <div data-id="element-2653">
              <h3 className="text-sm text-gray-500" data-id="element-2654">Max. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2655">
                {campaign.currency}{' '}
                {campaign.maxTransactionValue.toLocaleString()}
              </p>
            </div>
            <div data-id="element-2656">
              <h3 className="text-sm text-gray-500" data-id="element-2657">Min. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2658">
                {campaign.currency} {campaign.minDiscount.toFixed(2)}
              </p>
            </div>
            <div data-id="element-2659">
              <h3 className="text-sm text-gray-500" data-id="element-2660">Max. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2661">
                {campaign.currency} {campaign.maxDiscount.toFixed(2)}
              </p>
            </div>
            <div data-id="element-2662">
              <h3 className="text-sm text-gray-500" data-id="element-2663">Valid MCC</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2664">
                {campaign.validMCC?.join(', ') || 'All MCCs'}
              </p>
            </div>
            <div data-id="element-2665">
              <h3 className="text-sm text-gray-500" data-id="element-2666">Valid on days</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2667">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </p>
            </div>
            <div data-id="element-2668">
              <h3 className="text-sm text-gray-500" data-id="element-2669">Buyer Identifier</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2670">
                {campaign.validIdentities?.join(', ') || 'No information'}
              </p>
            </div>
            <div data-id="element-2671">
              <h3 className="text-sm text-gray-500" data-id="element-2672">Valid Identities</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2673">
                {campaign.validIdentities?.join(', ') || 'All identities'}
              </p>
            </div>
            <div data-id="element-2674">
              <h3 className="text-sm text-gray-500" data-id="element-2675">Code</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2676">
                {campaign.code}
              </p>
            </div>
            <div data-id="element-2677">
              <h3 className="text-sm text-gray-500" data-id="element-2678">Currency</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2679">
                {campaign.currency}
              </p>
            </div>
            <div data-id="element-2680">
              <h3 className="text-sm text-gray-500" data-id="element-2681">Discount Percentage</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2682">
                {campaign.discountPercentage}%
              </p>
            </div>
            <div data-id="element-2683">
              <h3 className="text-sm text-gray-500" data-id="element-2684">Fixed discount amount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2685">
                {campaign.currency} {campaign.fixedDiscountAmount.toFixed(2)}
              </p>
            </div>
            <div data-id="element-2686">
              <h3 className="text-sm text-gray-500" data-id="element-2687">Discount carried by</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2688">
                {campaign.discountCarriedBy}
              </p>
            </div>
            <div data-id="element-2689">
              <h3 className="text-sm text-gray-500" data-id="element-2690">
                Discount percentage carried by merchant
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2691">
                {campaign.merchantPercentage || 0}%
              </p>
            </div>
            <div data-id="element-2692">
              <h3 className="text-sm text-gray-500" data-id="element-2693">Budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2694">
                {campaign.currency} {campaign.budget.toLocaleString()}
              </p>
            </div>
            <div data-id="element-2695">
              <h3 className="text-sm text-gray-500" data-id="element-2696">Budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2697">
                {campaign.currency} {campaign.budgetUsed.toLocaleString()}
              </p>
            </div>
            <div data-id="element-2698">
              <h3 className="text-sm text-gray-500" data-id="element-2699">Recurring budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2700">
                {campaign.recurringBudget ? `${campaign.currency} ${campaign.recurringBudget.toLocaleString()}` : 'None'}
              </p>
            </div>
            <div data-id="element-2701">
              <h3 className="text-sm text-gray-500" data-id="element-2702">Recurring budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2703">
                {campaign.recurringBudgetUsed ? `${campaign.currency} ${campaign.recurringBudgetUsed.toLocaleString()}` : 'None'}
              </p>
            </div>
            <div data-id="element-2704">
              <h3 className="text-sm text-gray-500" data-id="element-2705">Recurring budget period</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2706">
                {campaign.recurringBudgetPeriod || 'No data available'}
              </p>
            </div>
            <div data-id="element-2707">
              <h3 className="text-sm text-gray-500" data-id="element-2708">
                Strictly enforce budget?
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2709">
                {campaign.strictlyEnforceBudget ? 'Yes' : 'No'}
              </p>
            </div>
            <div data-id="element-2710">
              <h3 className="text-sm text-gray-500" data-id="element-2711">Companies</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2712">
                {campaign.companies?.join(', ') || 'All companies'}
              </p>
            </div>
            <div data-id="element-2713">
              <h3 className="text-sm text-gray-500" data-id="element-2714">
                Limit usage per total customer
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-2715">
                {campaign.limitUsage || 'No information'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}